/**
 * Утилита для генерации уникального fingerprint браузера
 * Собирает информацию о браузере, операционной системе и устройстве
 * для создания уникального идентификатора пользователя
 */

/**
 * Получает информацию о браузере
 * @returns {Object} Информация о браузере
 */
const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const language = navigator.language || navigator.userLanguage;
    const platform = navigator.platform;
    const cookieEnabled = navigator.cookieEnabled;
    const doNotTrack = navigator.doNotTrack;
    
    return {
        userAgent,
        language,
        platform,
        cookieEnabled,
        doNotTrack,
        vendor: navigator.vendor || '',
        product: navigator.product || ''
    };
};

/**
 * Получает информацию об экране
 * @returns {Object} Информация об экране
 */
const getScreenInfo = () => {
    return {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        orientation: window.screen.orientation ? window.screen.orientation.type : 'unknown'
    };
};

/**
 * Получает информацию о часовом поясе
 * @returns {Object} Информация о часовом поясе
 */
const getTimezoneInfo = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneOffset = new Date().getTimezoneOffset();
    
    return {
        timezone,
        timezoneOffset,
        locale: Intl.DateTimeFormat().resolvedOptions().locale
    };
};

/**
 * Получает информацию о WebGL
 * @returns {Object} Информация о WebGL
 */
const getWebGLInfo = () => {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
            return { webgl: false };
        }
        
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        
        return {
            webgl: true,
            vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'unknown',
            renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown',
            version: gl.getParameter(gl.VERSION),
            shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION)
        };
    } catch (error) {
        return { webgl: false, error: error.message };
    }
};

/**
 * Получает информацию о Canvas
 * @returns {Object} Информация о Canvas
 */
const getCanvasInfo = () => {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Устанавливаем размеры canvas
        canvas.width = 200;
        canvas.height = 50;
        
        // Рисуем текст
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('Browser fingerprint', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Browser fingerprint', 4, 17);
        
        // Получаем данные canvas
        const canvasData = canvas.toDataURL();
        
        return {
            canvas: canvasData,
            width: canvas.width,
            height: canvas.height
        };
    } catch (error) {
        return { canvas: false, error: error.message };
    }
};

/**
 * Получает информацию о подключенных плагинах
 * @returns {Array} Список плагинов
 */
const getPluginsInfo = () => {
    const plugins = [];
    
    if (navigator.plugins) {
        for (let i = 0; i < navigator.plugins.length; i++) {
            const plugin = navigator.plugins[i];
            plugins.push({
                name: plugin.name,
                description: plugin.description,
                filename: plugin.filename,
                version: plugin.version
            });
        }
    }
    
    return plugins;
};

/**
 * Получает информацию о подключенных шрифтах
 * @returns {Promise<Array>} Список доступных шрифтов
 */
const getFontsInfo = async () => {
    const fonts = [
        'Arial', 'Arial Black', 'Arial Narrow', 'Arial Rounded MT Bold',
        'Calibri', 'Cambria', 'Candara', 'Century Gothic', 'Comic Sans MS',
        'Consolas', 'Courier New', 'Franklin Gothic Medium', 'Georgia',
        'Impact', 'Lucida Console', 'Lucida Sans Unicode', 'Microsoft Sans Serif',
        'Palatino Linotype', 'Segoe UI', 'Tahoma', 'Times New Roman',
        'Trebuchet MS', 'Verdana', 'Webdings', 'Wingdings'
    ];
    
    const availableFonts = [];
    
    // Создаем тестовый элемент
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.left = '-9999px';
    testElement.style.fontSize = '72px';
    testElement.innerHTML = 'mmmmmmmmmmlli';
    
    document.body.appendChild(testElement);
    
    // Получаем базовые размеры
    const baseWidths = {};
    const baseHeights = {};
    
    for (const font of fonts) {
        testElement.style.fontFamily = font;
        const width = testElement.offsetWidth;
        const height = testElement.offsetHeight;
        
        if (!baseWidths[width]) baseWidths[width] = 0;
        if (!baseHeights[height]) baseHeights[height] = 0;
        
        baseWidths[width]++;
        baseHeights[height]++;
    }
    
    // Определяем наиболее частые размеры (это будут системные шрифты)
    const mostCommonWidth = Object.keys(baseWidths).reduce((a, b) => 
        baseWidths[a] > baseWidths[b] ? a : b
    );
    const mostCommonHeight = Object.keys(baseHeights).reduce((a, b) => 
        baseHeights[a] > baseHeights[b] ? a : b
    );
    
    // Проверяем каждый шрифт
    for (const font of fonts) {
        testElement.style.fontFamily = font;
        const width = testElement.offsetWidth;
        const height = testElement.offsetHeight;
        
        // Если размер отличается от системного, шрифт доступен
        if (width !== parseInt(mostCommonWidth) || height !== parseInt(mostCommonHeight)) {
            availableFonts.push(font);
        }
    }
    
    document.body.removeChild(testElement);
    
    return availableFonts;
};

/**
 * Получает информацию о WebRTC
 * @returns {Promise<Object>} Информация о WebRTC
 */
const getWebRTCInfo = async () => {
    return new Promise((resolve) => {
        try {
            if (!window.RTCPeerConnection) {
                resolve({ webrtc: false, reason: 'Not supported' });
                return;
            }
            
            const pc = new RTCPeerConnection({
                iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
            });
            
            pc.createDataChannel('');
            pc.createOffer().then(offer => {
                pc.setLocalDescription(offer);
            });
            
            pc.onicecandidate = (ice) => {
                if (ice && ice.candidate && ice.candidate.candidate) {
                    const candidate = ice.candidate.candidate;
                    const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/);
                    
                    pc.close();
                    
                    resolve({
                        webrtc: true,
                        localIP: ipMatch ? ipMatch[1] : 'unknown',
                        candidate: candidate
                    });
                }
            };
            
            // Таймаут для WebRTC
            setTimeout(() => {
                pc.close();
                resolve({ webrtc: true, localIP: 'timeout' });
            }, 1000);
            
        } catch (error) {
            resolve({ webrtc: false, error: error.message });
        }
    });
};

/**
 * Генерирует уникальный fingerprint браузера
 * @returns {Promise<string>} Хешированный fingerprint
 */
export const generateFingerprint = async () => {
    try {
        // Собираем всю информацию
        const browserInfo = getBrowserInfo();
        const screenInfo = getScreenInfo();
        const timezoneInfo = getTimezoneInfo();
        const webglInfo = getWebGLInfo();
        const canvasInfo = getCanvasInfo();
        const pluginsInfo = getPluginsInfo();
        const fontsInfo = await getFontsInfo();
        const webrtcInfo = await getWebRTCInfo();
        
        // Создаем объект с собранной информацией
        const fingerprintData = {
            browser: browserInfo,
            screen: screenInfo,
            timezone: timezoneInfo,
            webgl: webglInfo,
            canvas: canvasInfo,
            plugins: pluginsInfo,
            fonts: fontsInfo,
            webrtc: webrtcInfo,
            timestamp: Date.now(),
            url: window.location.href,
            referrer: document.referrer,
            title: document.title
        };
        
        // Преобразуем в строку и хешируем
        const fingerprintString = JSON.stringify(fingerprintData);
        
        // Простое хеширование (можно заменить на более сложное)
        let hash = 0;
        for (let i = 0; i < fingerprintString.length; i++) {
            const char = fingerprintString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        
        // Возвращаем положительное число в виде строки
        const fingerprint = Math.abs(hash).toString(16);
        
        console.log('Generated fingerprint:', fingerprint);
        console.log('Fingerprint data:', fingerprintData);
        
        return fingerprint;
        
    } catch (error) {
        console.error('Error generating fingerprint:', error);
        
        // Fallback fingerprint на основе базовой информации
        const fallbackData = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            screen: `${window.screen.width}x${window.screen.height}`,
            timestamp: Date.now()
        };
        
        const fallbackString = JSON.stringify(fallbackData);
        let hash = 0;
        for (let i = 0; i < fallbackString.length; i++) {
            const char = fallbackString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        
        return Math.abs(hash).toString(16);
    }
};

/**
 * Сохраняет fingerprint в localStorage
 * @param {string} fingerprint - Сгенерированный fingerprint
 */
export const saveFingerprint = (fingerprint) => {
    try {
        localStorage.setItem('browser_fingerprint', fingerprint);
        localStorage.setItem('fingerprint_timestamp', Date.now().toString());
        console.log('Fingerprint saved to localStorage');
    } catch (error) {
        console.error('Error saving fingerprint:', error);
    }
};

/**
 * Получает сохраненный fingerprint из localStorage
 * @returns {string|null} Сохраненный fingerprint или null
 */
export const getSavedFingerprint = () => {
    try {
        const fingerprint = localStorage.getItem('browser_fingerprint');
        const timestamp = localStorage.getItem('fingerprint_timestamp');
        
        if (fingerprint && timestamp) {
            // Проверяем, не устарел ли fingerprint (например, старше 30 дней)
            const age = Date.now() - parseInt(timestamp);
            const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 дней
            
            if (age < maxAge) {
                return fingerprint;
            } else {
                // Удаляем устаревший fingerprint
                localStorage.removeItem('browser_fingerprint');
                localStorage.removeItem('fingerprint_timestamp');
            }
        }
        
        return null;
    } catch (error) {
        console.error('Error getting saved fingerprint:', error);
        return null;
    }
};

/**
 * Получает или генерирует новый fingerprint
 * @returns {Promise<string>} Fingerprint браузера
 */
export const getFingerprint = async () => {
    try {
        // Сначала пытаемся получить сохраненный fingerprint
        const savedFingerprint = getSavedFingerprint();
        
        if (savedFingerprint) {
            console.log('Using saved fingerprint:', savedFingerprint);
            return savedFingerprint;
        }
        
        // Если сохраненного нет, генерируем новый
        const newFingerprint = await generateFingerprint();
        saveFingerprint(newFingerprint);
        
        return newFingerprint;
        
    } catch (error) {
        console.error('Error getting fingerprint:', error);
        
        // Fallback на случай ошибки
        const fallbackFingerprint = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        saveFingerprint(fallbackFingerprint);
        
        return fallbackFingerprint;
    }
};

export default {
    generateFingerprint,
    saveFingerprint,
    getSavedFingerprint,
    getFingerprint
};
